import { makeStyles } from '@material-ui/core/styles';
import '@progress/kendo-theme-material/dist/all.css';
// import * as Sentry from '@sentry/react';
// import { Integrations } from '@sentry/tracing';
import mixpanel from 'mixpanel-browser';
import Oidc, { UserManager } from 'oidc-client';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from 'react-router-dom';
import { AppToolchainProvider } from 'src/components/providers/AppToolchainProvider';
import AppErrorPage from 'src/routes/AppErrorPage';
import { makeAPI } from 'src/networking/api';
import AppRoute from 'src/routes/AppRoute';
import LoginPage from 'src/routes/LoginPage';
import OidcSignin from 'src/routes/OidcSignin';
import OidcSigninRenew from 'src/routes/OidcSigninRenew';
import OidcSignout from 'src/routes/OidcSignout';
import { AppConfigOptions } from 'src/types/applicationTypes';
import ulog from 'ulog';
import CookieSettingContButton from 'src/components/CookieSettingContButton';
import LoginAsSupport from './routes/LoginAsSupport';
import LoginLandingPage from './routes/LoginLandingPage';
import { Provider } from 'react-redux';
import { store } from 'src/redux/store';
import { LicenseInfo } from '@mui/x-date-pickers-pro';
import SideNav from './components/SideNav';
import DetectionRoute from './routes/DetectionRoute';
import LoadingIndicator from './lib/components/LoadingIndicator';
import OnboardingRoute from './routes/OnboardingRoute';
import SettingsRoute from './routes/SettingsRoute';
import { withAdminProtection } from './components/withAdminProtection';

// Lazy loaded routes
const ScanlogRoute = lazy(() => import('./routes/ScanLogsRoute'));
const AuditlogRoute = lazy(() => import('./routes/AuditLogRoute'));
const CaseRoute = lazy(() => import('./routes/CaseRoute'));
const ReportsRoute = lazy(
	() => import('./routes/unifiedreporting/ReportsRoute'),
);

// Wrapping admin-only routes with protection HOC
const ProtectedScanlogRoute = withAdminProtection(ScanlogRoute);
const ProtectedAuditlogRoute = withAdminProtection(AuditlogRoute);
const ProtectedCaseRoute = withAdminProtection(CaseRoute);
const ProtectedReportsRoute = withAdminProtection(ReportsRoute);

const useStyles = makeStyles(() => ({
	'@global': {
		'html, body, #main, #main > main': {
			height: '100%',
			position: 'relative',
			overflowY: 'hidden',
		},
	},
}));

function App({ config }: { config: AppConfigOptions }): React.ReactElement {
	useStyles();

	// We have to save this as state here so we can use it
	// after an actual render has taken place (which will change the window location)
	const [searchParams] = useState(
		new URLSearchParams(window.location.search),
	);

	const [oidcUserManager] = useState<UserManager>(
		new UserManager({
			authority: `${config.oidcAuthority}`,
			userStore: new Oidc.WebStorageStateStore({
				store: window.localStorage,
			}),
			client_id: 'webui',
			redirect_uri: `${config.oidcRedirectUri}`,
			response_type: 'code',
			scope: 'frontendapi openid',
			post_logout_redirect_uri: `${config.oidcPostLogoutRedirectUri}`,
			automaticSilentRenew: true,
			silent_redirect_uri: `${config.oidcSigninRenewUri}`,
			loadUserInfo: true,
		}),
	);

	if (config.mixpanelProjectCode) {
		mixpanel.init(config.mixpanelProjectCode, {
			sanitize_properties: function (props) {
				const urlKeys = ['$current_url', '$initial_referrer', '$referrer'];
				urlKeys.forEach((key) => {
					if (props[key]) {
						try {
							const url = new URL(props[key]);
							url.searchParams.delete('code');
							url.searchParams.delete('state');
							url.searchParams.delete('session_state');
							props[key] = url.toString();
						} catch (e) {
							// not a valid URL, leave as-is
						}
					}
				});
				return props;
			},
		});
	}

	useEffect(() => {
		LicenseInfo.setLicenseKey(process.env.MUI_X_LICENSE_KEY || '');
	}, []);

	useEffect(() => {
		// Setup Mixpanel
	}, [config.mixpanelProjectCode]);

	// useEffect((): void => {
	// 	// Set up Sentry
	// 	ulog.debug('Starting Sentry.');
	// 	Sentry.init({
	// 		autoSessionTracking: true,
	// 		dsn: config.sentryDsn,
	// 		environment: config.codeusEnvironment,
	// 		release: process.env.REACT_APP_VERSION,
	// 		integrations: [new Integrations.BrowserTracing()],
	//
	// 		// We recommend adjusting this value in production, or using tracesSampler
	// 		// for finer control
	// 		tracesSampleRate: 1.0,
	// 	});
	// }, [config.sentryDsn, config.codeusEnvironment]);

	useEffect(() => {
		// Set up Google Analytics
		if (config.googleTrackingCode) {
			ReactGA.initialize(config.googleTrackingCode, {
				gaOptions: {
					siteSpeedSampleRate: 100,
				},
			});
		}

		const campaignSource = searchParams.get('utm_source');
		const campaignMedium = searchParams.get('utm_medium');
		const campaignName = searchParams.get('utm_campaign');

		if (window?.ga && (campaignSource || campaignMedium || campaignName)) {
			ulog.debug(
				'Setting campaign info:',
				campaignSource,
				campaignMedium,
				campaignName,
			);

			const campaign_information = {
				campaignSource,
				campaignMedium,
				campaignName,
			};

			ReactGA.set(campaign_information);
		}
	}, [config.googleTrackingCode]);

	useEffect(() => {
		if (config.cookielawDomainId) {
			setTimeout(() => {
				const script = document.createElement('script');
				script.type = 'text/javascript';
				script.setAttribute(
					'src',
					'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
				);
				script.setAttribute('charset', 'UTF-8');

				// Change to their data-domain-script
				script.setAttribute(
					'data-domain-script',
					config.cookielawDomainId,
				);
				document.getElementsByTagName('head')[0].appendChild(script);
			}, 1000);
		}
	}, [config.cookielawDomainId]);

	useEffect(() => {
		window.onbeforeunload = function () {
			window.sessionStorage.clear();
		};

		window.onunhandledrejection = (err: PromiseRejectionEvent) => {
			if (
				err.reason?.status === 401 &&
				window.location.pathname !== '/login' &&
				window.location.pathname !== '/bcclogin'
			) {
				// Sentry.configureScope((scope) => scope.setUser(null));
				oidcUserManager.clearStaleState();
				sessionStorage.clear();
				window.location.href = '/bcclogin';
			}
		};
	}, []);

	// This is the API object which we will use throughout the application.
	// It is pre-bound to all API actions, so we should not need to import
	// any action or http modules directly.
	const api = makeAPI(config.apiEndpoint, oidcUserManager);

	return (
		<Provider store={store}>
			<Router>
				<CookieSettingContButton></CookieSettingContButton>
				<AppToolchainProvider
					toolchain={{
						config,
						api,
						oidcUserManager,
					}}
				>
					<Suspense
						fallback={
							<LoadingIndicator
								message="Loading..."
								loaderSize={100}
							/>
						}
					>
						<Routes>
							{/* Public Routes - No Layout */}
							<Route
								path="/signin-oidc"
								element={<OidcSignin />}
							/>
							<Route path="/error" element={<AppErrorPage />} />
							<Route
								path="/signin-renew"
								element={<OidcSigninRenew />}
							/>
							<Route
								path="/signout-oidc"
								element={<OidcSignout />}
							/>
							<Route path="/bcclogin" element={<LoginPage />} />
							<Route
								path="/login"
								element={<LoginLandingPage />}
							/>
							<Route
								path="/delegatelogin"
								element={<LoginAsSupport />}
							/>

							{/* Protected Routes with BaseLayout (Header only, no sidebar) */}
							<Route element={<AppRoute />}>
								{/* Onboarding - Header only, no sidebar */}
								<Route
									path="/welcome"
									element={<OnboardingRoute />}
								/>

								{/* Routes with SideNav Layout (Header + Sidebar) */}
								<Route element={<SideNav />}>
									<Route
										path="/detections"
										element={<DetectionRoute />}
									/>
									{/* Admin-only routes */}
									<Route
										path="/scanlog"
										element={<ProtectedScanlogRoute />}
									/>
									<Route
										path="/auditlog"
										element={<ProtectedAuditlogRoute />}
									/>
									<Route
										path="/cases/:id"
										element={<ProtectedCaseRoute />}
									/>
									<Route
										path="/reports/*"
										element={<ProtectedReportsRoute />}
									/>
									<Route
										path="/settings/*"
										element={<SettingsRoute />}
									/>

									{/* Default redirect */}
									<Route
										path="/"
										element={
											<Navigate
												to="/detections"
												replace
											/>
										}
									/>
								</Route>
							</Route>
							{/* Wildcard - redirect to detections */}
							<Route
								path="*"
								element={<Navigate to="/detections" replace />}
							/>
						</Routes>
					</Suspense>
				</AppToolchainProvider>
			</Router>
		</Provider>
	);
}

export default App;
